import React, { useState, useEffect, useMemo } from 'react';
import '../styles/zoom-admin.css';
import { useBranding } from '../context/BrandingContext';
import axios from 'axios';
import { useCustomer } from '../components/customerCtx';
import liveEvents from '../services/LiveEvents';

/* ============================================================================
   Zoom-style admin shell — rendered ONLY for the TEC tenant (gated in
   AdminShell.tsx). It is a drop-in replacement for the default shell:
   it receives the identical `active` / `setActive` contract and emits the
   EXACT same page strings, so every page component and all behaviour is
   unchanged. Only the navigation chrome + theme differ.
   ============================================================================ */

/* Incoming-call bar (2026-10-28): the phone tab (/v2/, same site) announces its calls on BroadcastChannel
   'ce-phone'; while an admin works here they can answer / decline / hang up through that tab (audio stays there). */
function IncomingCallBar() {
  const [c, setC] = useState<any>(null);
  const [hint, setHint] = useState(false);
  const chRef = React.useRef<any>(null);
  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;
    const ch = new (window as any).BroadcastChannel('ce-phone'); chRef.current = ch;
    ch.onmessage = (ev: any) => {
      const d = ev.data || {};
      if (d.type !== 'call') return;
      if (d.direction === 'incoming' && d.state === 'ringing') setC(d);
      else if (d.state === 'connected') setC({ ...d, live: true });
      else { setC(null); setHint(false); }
    };
    try { ch.postMessage({ cmd: 'ping' }); } catch (_) {}
    return () => { ch.close(); chRef.current = null; };
  }, []);
  if (!c) return null;
  const send = (cmd: string) => { try { chRef.current && chRef.current.postMessage({ cmd }); } catch (_) {} };
  // Answer: browsers give the microphone only to the tab in front, so bring the phone tab forward
  // (a click may do that) — the phone answers as soon as it is visible.
  const answer = () => {
    send('answer');
    try {
      const w = window.open('', 'ce-phone');
      if (w && w.location && w.location.href === 'about:blank') { w.close(); setHint(true); }
      else if (w) { try { w.focus(); } catch (_) {} }
      else if (!(window as any).cePhone) setHint(true);   // desktop app: the wrapper brings the phone window forward
    } catch (_) { if (!(window as any).cePhone) setHint(true); }
  };
  const who = c.name && c.name !== c.number ? `${c.name} (${c.number})` : c.number || 'Unknown caller';
  const btn = (bg: string): React.CSSProperties => ({ border: 'none', borderRadius: 8, padding: '7px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', color: '#fff', background: bg });
  return (
    <div role="alert" style={{ position: 'fixed', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, display: 'flex', alignItems: 'center', gap: 12,
      background: c.live ? '#0f766e' : '#1d4ed8', color: '#fff', padding: '10px 16px', borderRadius: 12, boxShadow: '0 10px 30px rgba(0,0,0,.25)', fontSize: 14, maxWidth: 'calc(100vw - 32px)' }}>
      <span>{c.live ? 'On a call with ' : (c.queue ? `Incoming call · ${c.queue} — ` : 'Incoming call — ')}<b>{who}</b></span>
      {c.live
        ? <button style={btn('#b91c1c')} onClick={() => send('hangup')}>Hang up</button>
        : <><button style={btn('#16a34a')} onClick={answer}>Answer</button><button style={btn('#b91c1c')} onClick={() => send('decline')}>Decline</button></>}
      {hint && !c.live && <span style={{ fontSize: 12, opacity: 0.9 }}>Switch to the <b>CallEssential Phone</b> tab — it answers there.</span>}
    </div>
  );
}

/* Reception card (2026-10-30): receptionists who work in this console (e.g. the Operations team) also get the
   "call for <customer> — read this" card that the phone app shows. Same server message (reception_call). */
function ReceptionCards() {
  const [cards, setCards] = useState<any[]>([]);
  useEffect(() => {
    const onCall = (msg: any) => {
      const p = (msg && msg.payload) || {};
      if (!p.call_uuid) return;
      setCards((cs) => [{ ...p, state: 'ringing' }, ...cs.filter((c) => c.call_uuid !== p.call_uuid)].slice(0, 3));
    };
    const onEnd = (msg: any) => {
      const p = (msg && msg.payload) || {};
      if (!p.call_uuid) return;
      if (p.reason === 'not_answered_here') {
        setCards((cs) => cs.map((c) => (c.call_uuid === p.call_uuid ? { ...c, state: 'elsewhere' } : c)));
        setTimeout(() => setCards((cs) => cs.filter((c) => c.call_uuid !== p.call_uuid)), 2500);
      } else setCards((cs) => cs.filter((c) => c.call_uuid !== p.call_uuid));
    };
    liveEvents.on('reception_call', onCall);
    liveEvents.on('reception_call_end', onEnd);
    return () => { liveEvents.off('reception_call', onCall); liveEvents.off('reception_call_end', onEnd); };
  }, []);
  if (!cards.length) return null;
  return (
    <div role="status" aria-live="assertive" style={{ position: 'fixed', top: 64, right: 16, zIndex: 2999, display: 'flex', flexDirection: 'column', gap: 10, width: 360, maxWidth: 'calc(100vw - 32px)' }}>
      {cards.map((c) => (
        <div key={c.call_uuid} style={{ background: '#fff', border: '1px solid #c7d2fe', borderLeft: '5px solid #4f46e5', borderRadius: 12, boxShadow: '0 12px 32px rgba(15,23,42,.22)', padding: '12px 14px', opacity: c.state === 'elsewhere' ? 0.55 : 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#4f46e5', textTransform: 'uppercase', letterSpacing: '.04em' }}>{c.is_customer ? 'Customer call' : 'Incoming call'}</span>
            <span style={{ flex: 1 }} />
            <button aria-label="Close" onClick={() => setCards((cs) => cs.filter((x) => x.call_uuid !== c.call_uuid))} style={{ border: 'none', background: 'none', fontSize: 18, lineHeight: 1, cursor: 'pointer', color: '#6b7280' }}>×</button>
          </div>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#111827', margin: '2px 0 6px' }}>{c.customer}{c.code ? ` (${c.code})` : ''}</div>
          {c.script_en && <div style={{ fontSize: 14, color: '#1f2937', background: '#f5f7ff', borderRadius: 8, padding: '8px 10px' }}>{c.script_en}</div>}
          {c.script_ar && <div dir="rtl" lang="ar" style={{ fontSize: 15, color: '#1f2937', background: '#f5f7ff', borderRadius: 8, padding: '8px 10px', marginTop: 6 }}>{c.script_ar}</div>}
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <span>Caller: <b>{c.caller || 'Unknown'}</b></span>
            {c.dialled && <span>Dialled: <b>{c.dialled}</b></span>}
            {c.state === 'elsewhere' && <span style={{ color: '#b45309', fontWeight: 600 }}>Answered elsewhere</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

type Group = { id: string; label: string; icon: keyof typeof ICONS; items: string[]; open: boolean };

// Every item below is one of the existing TENANT_ADMIN page strings — nothing
// is added or removed, only regrouped/relabelled to mirror the Zoom console.
const TENANT_GROUPS: Group[] = [
  { id: 'dashboard', label: 'Dashboard',              icon: 'grid',   items: ['Dashboard', 'Wallboard'], open: true },
  { id: 'users',     label: 'User Management',        icon: 'users',  items: ['Customers', 'Reception Settings', 'Users', 'User Groups', 'Operations Team', 'Company Directory', 'External Contacts'], open: true },
  { id: 'phone',     label: 'Phone System Management',icon: 'phone',  items: ['Live Calls', 'Extensions', 'Voicemail', 'DIDs', 'Call Flows', 'Greetings', 'Sites', 'Phones', 'Speed Dials', 'Trunks', 'Pickup Groups', 'Voice Bots', 'Dialing Policy', 'Call Records', 'Call Trace'], open: true },
  { id: 'queues',    label: 'Queue Management',       icon: 'queue',  items: ['Queues', 'IVR Menus', 'Business Hours', 'Supervisor Monitor', 'Queue Monitor', 'Reports'], open: true },
  { id: 'branches',  label: 'Branches / Edge',        icon: 'link',   items: ['Edge Servers'], open: true },
  { id: 'ai',        label: 'AI Studio',              icon: 'ai',     items: ['AI Sales Agent', 'AI Queue Assistant', 'AI Conversations', 'AI Knowledge Base', 'Call Transcripts', 'Transcript Search', 'Transcription Admin', 'AI Reports', 'AI Settings'], open: false },
  { id: 'billing',   label: 'Billing',                icon: 'wallet', items: ['Billing Dashboard', 'Invoices', 'Rate Plans', 'Packages & Bundles', 'Billing Accounts', 'Billing Reports', 'DID Billing', 'Tax Settings'], open: false },
  { id: 'integ',     label: 'Integrations',           icon: 'link',   items: ['Teams Settings', 'Teams Contacts', 'WhatsApp', 'CRM Integrations', 'CRM Dashboard', 'CRM Queue Mapping', 'CRM Field Mapping'], open: false },
  { id: 'account',   label: 'Account Management',     icon: 'cog',    items: ['Tenant Branding', 'Managed Tenants', 'Licenses', 'Settings'], open: false },
];

// Super-admin nav — every super-admin page mapped once, Zoom-grouped. Ensures
// platform admins keep full access when the Zoom console is enabled for all.
const SUPER_ADMIN_GROUPS: Group[] = [
  { id: 'dashboard', label: 'Dashboard',               icon: 'grid',   items: ['Dashboard', 'Wallboard', 'Banned IPs'], open: true },
  { id: 'platform',  label: 'Platform',                icon: 'cog',    items: ['Tenants', 'Licenses', 'Telephony Engine'], open: true },
  { id: 'users',     label: 'User Management',         icon: 'users',  items: ['Customers', 'Reception Settings', 'Users', 'User Groups', 'Operations Team', 'Company Directory', 'External Contacts'], open: true },
  { id: 'phone',     label: 'Phone System Management', icon: 'phone',  items: ['Live Calls', 'Extensions', 'Voicemail', 'DIDs', 'Call Flows', 'Greetings', 'Sites', 'DID Inventory', 'Gateway Management', 'SBC Management', 'Phones', 'Speed Dials', 'Trunks', 'Pickup Groups', 'Voice Bots', 'Dialing Policy', 'Call Records', 'Call Trace'], open: false },
  { id: 'queues',    label: 'Queue Management',        icon: 'queue',  items: ['Queues', 'IVR Menus', 'Business Hours', 'Supervisor Monitor', 'Queue Monitor', 'Reports'], open: false },
  { id: 'ai',        label: 'AI Studio',               icon: 'ai',     items: ['AI Sales Agent', 'AI Queue Assistant', 'AI Conversations', 'AI Knowledge Base', 'Call Transcripts', 'Transcript Search', 'Transcription Admin', 'AI Reports', 'AI Settings'], open: false },
  { id: 'billing',   label: 'Billing',                 icon: 'wallet', items: ['Billing Dashboard', 'Billing Accounts', 'Rate Plans', 'Invoices', 'Billing Reports', 'Carriers & LCR', 'Packages & Bundles', 'DID Billing', 'Reseller Billing', 'Revenue Assurance', 'Tax Settings'], open: false },
  { id: 'integ',     label: 'Integrations',            icon: 'link',   items: ['Teams Settings', 'Teams Contacts', 'WhatsApp', 'Teams Admin', 'CRM Integrations', 'CRM Dashboard', 'CRM Queue Mapping', 'CRM Field Mapping'], open: false },
  { id: 'developer', label: 'Developer',               icon: 'cog',    items: ['Demo Platform', 'Deploy to Production'], open: false },
  { id: 'system',    label: 'System',                  icon: 'cog',    items: ['Security', 'Dialing Policy', 'Menu Access', 'Managed Tenants', 'Settings', 'Ops Knowledge Base', 'Tenant Branding'], open: false },
];

// Restricted nav for users with a per-user Menu Access list (menu_allowed).
// Item strings are INTERNAL page keys (e.g. 'Voice Bots' shows as 'Auto Receptionist').
const RESTRICTED_GROUPS: Group[] = [
  { id: 'r-dashboard', label: 'Dashboard',       icon: 'grid',  items: ['Dashboard', 'Wallboard'], open: true },
  { id: 'r-users',     label: 'User Management',  icon: 'users', items: ['Customers', 'Reception Settings', 'Users', 'User Groups', 'Operations Team', 'Company Directory', 'External Contacts'], open: true },
  { id: 'r-phone',     label: 'Phone Management', icon: 'phone', items: ['Live Calls', 'Extensions', 'Voicemail', 'DIDs', 'Call Flows', 'Greetings', 'Sites', 'Phones', 'Speed Dials', 'Pickup Groups', 'Voice Bots', 'AI Knowledge Base', 'Dialing Policy', 'Call Records', 'Call Trace'], open: true },
  { id: 'r-queues',    label: 'Queue Management', icon: 'queue', items: ['Queues', 'IVR Menus', 'Business Hours', 'Supervisor Monitor', 'Queue Monitor'], open: true },
  { id: 'r-reports',   label: 'Reports',          icon: 'queue', items: ['Reports'], open: false },
  { id: 'r-billing',   label: 'Billing',          icon: 'wallet', items: ['Billing Dashboard', 'Billing Accounts', 'Invoices', 'Billing Reports', 'Rate Plans', 'Packages & Bundles', 'DID Billing', 'Tax Settings'], open: false },
];
// PBX Administrator (pbx_admin): cross-tenant PBX manager via the tenant switcher.
// Gateway / SBC are read-only server-side.
const PBX_ADMIN_GROUPS: Group[] = [
  { id: 'p-dashboard', label: 'Dashboard',               icon: 'grid',   items: ['Dashboard', 'Wallboard'], open: true },
  { id: 'p-users',     label: 'User Management',         icon: 'users',  items: ['Customers', 'Reception Settings', 'Users', 'User Groups', 'Company Directory', 'External Contacts'], open: true },
  { id: 'p-phone',     label: 'Phone System Management', icon: 'phone',  items: ['Live Calls', 'Extensions', 'Voicemail', 'DIDs', 'Call Flows', 'Greetings', 'Sites', 'Phones', 'Speed Dials', 'Pickup Groups', 'Voice Bots', 'Dialing Policy', 'Call Records', 'Call Trace'], open: true },
  { id: 'p-gwsbc',     label: 'Gateway & SBC',           icon: 'link',   items: ['Gateway Management', 'SBC Management'], open: false },
  { id: 'p-queues',    label: 'Queue Management',        icon: 'queue',  items: ['Queues', 'IVR Menus', 'Business Hours', 'Supervisor Monitor', 'Queue Monitor', 'Reports'], open: true },
  { id: 'p-ai',        label: 'AI Studio',               icon: 'ai',     items: ['AI Sales Agent', 'AI Queue Assistant', 'AI Conversations', 'AI Knowledge Base', 'Call Transcripts', 'Transcript Search', 'Transcription Admin', 'AI Reports', 'AI Settings'], open: false },
  { id: 'p-billing',   label: 'Billing',                 icon: 'wallet', items: ['Billing Dashboard', 'Billing Accounts', 'Rate Plans', 'Invoices', 'Billing Reports', 'Packages & Bundles', 'DID Billing', 'Tax Settings'], open: false },
];
function buildRestrictedGroups(allowed: string[], billingPages?: string[] | null): Group[] {
  const set = new Set(allowed);
  // 'Billing' grants the Billing pages — limited to billingPages when the user's group says so (2026-10-23)
  const ok = (g: Group, i: string) => set.has(i) || (g.id === 'r-billing' && set.has('Billing') && (!billingPages || billingPages.includes(i)));
  return RESTRICTED_GROUPS
    .map((g) => ({ ...g, items: g.items.filter((i) => ok(g, i)) }))
    .filter((g) => g.items.length > 0);
}

// Friendlier display labels for nav items whose internal page string differs.
const NAV_LABELS: Record<string, string> = { 'Operations Team': 'Access groups', 'Greetings': 'Greetings & recordings', 'Voice Bots': 'Auto Receptionist', 'Business Hours': 'Business Hours & Holidays (advanced)', 'IVR Menus': 'IVR Menus (advanced)' };
const ADVANCED_ONLY = ['Business Hours', 'IVR Menus'];
// Platform-wide pages: hidden while managing another organisation (the server refuses them too)
const PLATFORM_ITEMS = ['Banned IPs', 'Tenants', 'Licenses', 'Telephony Engine', 'DID Inventory', 'Gateway Management', 'SBC Management',
  'Carriers & LCR', 'Reseller Billing', 'Revenue Assurance', 'Teams Admin', 'Demo Platform', 'Deploy to Production', 'Security',
  'Menu Access', 'Managed Tenants', 'Ops Knowledge Base', 'Health Check', 'Security Monitoring', 'Edge Servers'];

const ICONS: Record<string, React.ReactNode> = {
  grid:   (<><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></>),
  users:  (<><circle cx="9" cy="8" r="3"/><path d="M3.5 19a5.5 5.5 0 0 1 11 0"/><path d="M16 6.2a3 3 0 0 1 0 5.6M17.5 19a5.5 5.5 0 0 0-2-4.3"/></>),
  phone:  (<path d="M6.6 3.5 9 3l1.5 4.5-2 1.5a12 12 0 0 0 5 5l1.5-2L18.5 13l-.5 2.4a2 2 0 0 1-2.3 1.5A15 15 0 0 1 3.6 5.8 2 2 0 0 1 5.1 3.5z"/>),
  queue:  (<path d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01"/>),
  ai:     (<><rect x="5" y="6" width="14" height="12" rx="3"/><path d="M12 6V3M9 11h.01M15 11h.01M9.5 15h5"/><path d="M2 11v3M22 11v3"/></>),
  wallet: (<><rect x="3" y="6" width="18" height="13" rx="2.5"/><path d="M3 10h18M16 14h2"/></>),
  link:   (<><path d="M10 13a4 4 0 0 0 5.6.4l2.5-2.5a4 4 0 0 0-5.6-5.6L11 6.7"/><path d="M14 11a4 4 0 0 0-5.6-.4L5.9 13a4 4 0 0 0 5.6 5.6L12.9 17"/></>),
  cog:    (<><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.2 4.2l2.1 2.1M17.7 17.7l2.1 2.1M2 12h3M19 12h3M4.2 19.8l2.1-2.1M17.7 6.3l2.1-2.1"/></>),
  item:   (<circle cx="12" cy="12" r="3"/>),
};

// Derive the Zoom accent palette from the tenant's brand colour so the console
// wears the tenant's identity (e.g. TEC navy) instead of the generic Zoom blue.
function brandCssVars(hex?: string | null): React.CSSProperties {
  const base = (hex && /^#([0-9a-fA-F]{6})$/.test(hex)) ? hex : '#2D8CFF';
  const n = parseInt(base.slice(1), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  const mix = (c: number, t: number, amt: number) => Math.round(c + (t - c) * amt);
  const hx = (x: number) => x.toString(16).padStart(2, '0');
  const hover = `#${hx(mix(r, 0, 0.14))}${hx(mix(g, 0, 0.14))}${hx(mix(b, 0, 0.14))}`;
  const soft  = `#${hx(mix(r, 255, 0.90))}${hx(mix(g, 255, 0.90))}${hx(mix(b, 255, 0.90))}`;
  return {
    ['--z-blue' as any]: base,
    ['--z-blue-hover' as any]: hover,
    ['--z-blue-soft' as any]: soft,
    ['--ce-accent' as any]: base,
  };
}

function Icon({ name, className }: { name: keyof typeof ICONS; className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {ICONS[name] ?? ICONS.item}
    </svg>
  );
}

type Props = {
  user: any;
  onLogout: () => void;
  active: string;
  setActive: (page: string) => void;
  children: React.ReactNode;
};

export default function ZoomShell({ user, onLogout, active, setActive, children }: Props) {
  const branding = useBranding();

  // Zoom is a light console — force light theme while this shell is mounted,
  // restoring whatever was set before on unmount (e.g. logout). Scoped to the
  // TEC session only because this component is only rendered for that tenant.
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.getAttribute('data-theme');
    root.setAttribute('data-theme', 'light');
    return () => {
      if (prev === null) root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', prev);
    };
  }, []);

  const isSuperAdmin = user?.role === 'super_admin' || user?.is_admin === true || user?.is_admin === 1;
  const menuAllowed = Array.isArray(user?.menu_allowed) && user.menu_allowed.length > 0 ? user.menu_allowed : null;
  const _groupsAll = menuAllowed
    ? buildRestrictedGroups(menuAllowed, user?.access_profile?.restrictions?.billing_pages || null)
    : user?.role === 'pbx_admin'
    ? PBX_ADMIN_GROUPS
    : (isSuperAdmin ? SUPER_ADMIN_GROUPS : TENANT_GROUPS);
  // Customers (multi-customer tenant): TEC, or any organisation with customer accounts (2026-10-29)
  const _cust = useCustomer();
  const _custOk = isSuperAdmin || Number(user?.tenant_id) === 2 || _cust.enabled;
  // Managing another organisation through the switcher: only that organisation's pages (2026-10-29).
  // Platform pages come back when the switcher is set to the own organisation.
  const _foreign = (() => { try { const a = localStorage.getItem('ce_act_as_tenant'); return !!a && Number(a) !== Number(user?.tenant_id); } catch { return false; } })();
  // Health Check + Security Monitoring: shown only when the server allowlist says so
  // Advanced pages (Business Hours, IVR Menus) only for advanced users; ops use Call Flows + queue Hours & holidays
  const [advOk, setAdvOk] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get('/api/ui/advanced').then((r) => { if (alive) setAdvOk(!!r.data?.advanced); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const [hcOk, setHcOk] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get('/api/health-check/access').then((r) => { if (alive) setHcOk(!!r.data?.allowed); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Call Trace (2026-10-29): only when the server allows it (TEC admins, super admins, groups with the menu)
  const [ctOk, setCtOk] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get('/api/call-trace/access').then((r) => { if (alive) setCtOk(!!r.data?.allowed); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Speed Dials (2026-11-04): only organisations the server enables (TEC)
  const [sdOk, setSdOk] = useState(false);
  useEffect(() => {
    let alive = true;
    axios.get('/api/speed-dials/access').then((r) => { if (alive) setSdOk(!!r.data?.allowed); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  // Company Directory / External Contacts: tenant + super admins only (2026-10-27)
  const _dirOk = isSuperAdmin || user?.role === 'tenant_admin';
  const groups = _groupsAll
    .map((g) => (_custOk ? g : { ...g, items: g.items.filter((i) => i !== 'Customers' && i !== 'Reception Settings') }))
    .map((g) => (ctOk ? g : { ...g, items: g.items.filter((i) => i !== 'Call Trace') }))
    .map((g) => (sdOk ? g : { ...g, items: g.items.filter((i) => i !== 'Speed Dials') }))
    .map((g) => (_dirOk ? g : { ...g, items: g.items.filter((i) => i !== 'Company Directory' && i !== 'External Contacts') }))
    .map((g) => (hcOk && /dashboard$/.test(g.id) ? { ...g, items: [...g.items, 'Health Check', 'Security Monitoring'] } : g))
    .map((g) => (advOk ? g : { ...g, items: g.items.filter((i) => !ADVANCED_ONLY.includes(i)) }))
    .map((g) => (_foreign ? { ...g, items: g.items.filter((i) => !PLATFORM_ITEMS.includes(i)) } : g))
    .filter((g) => g.items.length > 0);

  const storageKey = isSuperAdmin ? 'zoom-sidebar-open-sa' : 'zoom-sidebar-open';
  const [open, setOpen] = useState<Record<string, boolean>>(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const init: Record<string, boolean> = {};
      groups.forEach((g) => { init[g.id] = g.id in saved ? saved[g.id] : g.open; });
      return init;
    } catch {
      const init: Record<string, boolean> = {};
      groups.forEach((g) => { init[g.id] = g.open; });
      return init;
    }
  });

  const activeGroupId = useMemo(
    () => groups.find((g) => g.items.includes(active))?.id ?? '',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [active]
  );

  // Auto-open the group holding the active page
  useEffect(() => {
    if (activeGroupId && !open[activeGroupId]) {
      setOpen((p) => ({ ...p, [activeGroupId]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, activeGroupId]);

  useEffect(() => {
    try { localStorage.setItem(storageKey, JSON.stringify(open)); } catch {}
  }, [open]);

  const [search, setSearch] = useState('');
  // ── Managed-tenant switcher (Zoom-style) ──────────────────────────────────
  const [acctTenants, setAcctTenants] = useState<any[]>([]);
  const [homeTenantId, setHomeTenantId] = useState<number | null>(null);
  const actingTenant = (() => { try { return localStorage.getItem('ce_act_as_tenant'); } catch { return null; } })();
  useEffect(() => {
    let alive = true;
    axios.get('/api/my/tenants').then((r) => {
      if (!alive) return;
      setAcctTenants(r.data?.tenants || []);
      setHomeTenantId(r.data?.homeTenantId ?? null);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const onSwitchTenant = (val: string) => {
    try {
      if (!val || (homeTenantId != null && Number(val) === Number(homeTenantId))) localStorage.removeItem('ce_act_as_tenant');
      else localStorage.setItem('ce_act_as_tenant', String(val));
    } catch {}
    window.location.reload();
  };
  const allItems = groups.flatMap((g) => g.items);
  const results = search.trim()
    ? allItems.filter((i) => i.toLowerCase().includes(search.toLowerCase()))
    : null;

  const brandName = branding.companyName || 'CallEssential';
  const displayName = user?.display_name || user?.username || 'Admin';
  const avatarChar = String(user?.username || 'A').substring(0, 1).toUpperCase();

  return (
    <div className="zoom-shell" style={brandCssVars(branding.primaryColor)}>
      {/* ── Sidebar ── */}
      <aside className="z-sidebar">
        <div className="z-brand">
          {branding.logo
            ? <img src={branding.logo} alt={brandName} />
            : <span className="z-brand-name">{brandName}</span>}
        </div>

        <div className="z-search">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search settings…"
          />
        </div>

        <div className="z-caption">Admin</div>

        <nav className="z-menu">
          {results ? (
            results.length ? (
              results.map((item) => (
                <div
                  key={item}
                  className={`z-item flat${active === item ? ' active' : ''}`}
                  onClick={() => { setActive(item); setSearch(''); }}
                >
                  <Icon name="item" className="z-ic" />
                  <span>{NAV_LABELS[item] || item}</span>
                </div>
              ))
            ) : (
              <div className="z-empty">No results for “{search}”</div>
            )
          ) : (
            groups.map((g) => {
              const isOpen = !!open[g.id];
              const hasActive = g.items.includes(active);
              return (
                <div key={g.id} className="z-group">
                  <button
                    className={`z-group-head${hasActive ? ' has-active' : ''}`}
                    onClick={() => setOpen((p) => ({ ...p, [g.id]: !p[g.id] }))}
                    title={g.label}
                  >
                    <Icon name={g.icon} className="z-ic" />
                    <span className="z-group-label">{g.label}</span>
                    <span className="z-arrow" style={{ transform: isOpen ? 'rotate(90deg)' : 'none' }}>›</span>
                  </button>
                  <div
                    className="z-group-items"
                    style={{ maxHeight: isOpen ? `${g.items.length * 40}px` : '0px' }}
                  >
                    {g.items.map((item) => (
                      <div
                        key={item}
                        className={`z-item${active === item ? ' active' : ''}`}
                        onClick={() => setActive(item)}
                      >
                        <span>{NAV_LABELS[item] || item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </nav>
      </aside>

      {/* ── Main ── */}
      <div className="z-main">
        <IncomingCallBar />
        <ReceptionCards />
        <header className="z-topbar">
          <div className="z-title">{NAV_LABELS[active] || active}</div>
          <div className="z-top-right">
            {acctTenants.length > 1 && (
              <select
                className="z-tenant-switch"
                value={actingTenant || (homeTenantId ?? '')}
                onChange={(e) => onSwitchTenant(e.target.value)}
                title="Switch the tenant you are managing"
                style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #d0d5dd', fontWeight: 600, fontSize: 13, marginRight: 14, maxWidth: 230,
                         background: (actingTenant && homeTenantId != null && Number(actingTenant) !== Number(homeTenantId)) ? '#fff7ed' : '#fff',
                         color: (actingTenant && homeTenantId != null && Number(actingTenant) !== Number(homeTenantId)) ? '#b45309' : 'inherit' }}
              >
                {acctTenants.map((t: any) => (
                  <option key={t.id} value={t.id}>
                    {Number(t.id) === Number(homeTenantId) ? `${t.company_name || t.name} (home)` : `Managing: ${t.company_name || t.name}`}
                  </option>
                ))}
              </select>
            )}
            {(user as any)?.extension_number && (
              <a
                className="z-phone-btn"
                href="/v2/"
                title="Back to your phone (dial pad, calls, chat)"
                onClick={(e) => {
                  // 2026-10-28: this console was opened in its own tab from the phone (window name 'ce-admin') —
                  // go back to that still-registered phone tab instead of loading a second phone here
                  try {
                    if (window.name === 'ce-admin' && window.opener && !window.opener.closed) {
                      e.preventDefault();
                      try { window.opener.focus(); } catch (_) {}
                      window.close();
                    }
                  } catch (_) {}
                }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: '1px solid #d0d5dd',
                         background: '#fff', color: '#1f2733', fontWeight: 600, fontSize: 13, marginRight: 14, textDecoration: 'none', whiteSpace: 'nowrap' }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.18 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.1 9.9a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
                </svg>
                Phone
              </a>
            )}
            <div className="z-user-meta">
              <div className="z-user-name">{displayName}</div>
              <div className="z-user-role">{user?.role || 'Administrator'}</div>
            </div>
            <div className="z-avatar">{avatarChar}</div>
            <button className="z-logout" onClick={onLogout}>Logout</button>
          </div>
        </header>

        <div className="z-content">
          {children}
        </div>
      </div>
    </div>
  );
}
